/**
 * The last line of defence against a blank page.
 *
 * React unmounts the whole tree when a render throws and nothing catches it, so
 * a single unexpected error anywhere in the app would leave the user staring at
 * an empty document — with no UI and no clue. This boundary catches that error
 * and renders a short, non-technical message instead, which is also where the
 * user learns the reassuring part: the trajectory is not gone, because it lives
 * in this browser and in the page's address.
 *
 * It deliberately does nothing else. No reporting service, no network: the only
 * trace is a console log. The thrown value is not inspected at all — `throw
 * 'boom'` must be as survivable as an `Error` — so the fallback renders from
 * static text and can never throw itself.
 */

import { Component } from 'react'
import type { ErrorInfo, ReactElement, ReactNode } from 'react'

export interface ErrorBoundaryProps {
  children: ReactNode
}

interface ErrorBoundaryState {
  failed: boolean
}

export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { failed: false }

  /**
   * Flip to the fallback. The error argument is deliberately not read: the state
   * depends only on *that* something threw, and the thrown value may be anything
   * at all.
   */
  static getDerivedStateFromError(): ErrorBoundaryState {
    return { failed: true }
  }

  /**
   * Log only — no external reporting, no network. The thrown value is passed
   * through untouchable rather than having `.message` read off it, because it
   * does not have to be an `Error`.
   */
  componentDidCatch(error: unknown, info: ErrorInfo): void {
    console.error('Unhandled error in the app', error, info.componentStack)
  }

  render(): ReactNode {
    if (this.state.failed) return <ErrorFallback />
    return this.props.children
  }
}

/**
 * What the user sees instead of the app.
 *
 * Built from the app's existing classes and tokens (`app`, `app__layout`, `card`,
 * `app__title`, `app__lede`, `button button--primary`) so it looks like the page
 * it replaces, and the one inline rule is a grid gap in token units because the
 * fallback has no stylesheet of its own (both `index.css` and `styles/app.css`
 * are already loaded in the document). The layout is a single column that wraps,
 * so it reads the same on a phone as on a desktop.
 */
function ErrorFallback(): ReactElement {
  return (
    <div className="app" data-testid="error-fallback">
      <div className="app__layout">
        <section
          className="card"
          role="alert"
          style={{ display: 'grid', gap: 'var(--space-4)', justifyItems: 'start' }}
        >
          <h1 className="app__title">Something went wrong</h1>
          <p className="app__lede">
            The page hit an unexpected error and stopped. Nothing is lost — your history is in
            this page&rsquo;s address, so reloading brings you back.
          </p>
          <button type="button" className="button button--primary" onClick={reloadPage}>
            Reload the page
          </button>
        </section>
      </div>
    </div>
  )
}

/** Reload is the only recovery action the app can offer from here. */
function reloadPage(): void {
  window.location.reload()
}
